import { useQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { getPerformanceLeaderboard, getPerformanceTeamSummary } from "../api/endpoints/performance"
import { AgentPerformanceDrawer } from "../components/agent_performance_drawer"
import { PerformanceBandBadge } from "../components/performance_band_badge"
import { PipelineMiniBar } from "../components/pipeline_mini_bar"
import { TrendSparkline } from "../components/trend_sparkline"
import {
  Button,
  DataTable,
  EmptyState,
  Input,
  PageSection,
  StatCard,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
} from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDateTime } from "../utils/formatting"

function metricLabel(metric: string) {
  switch (metric) {
    case "recoveredAmount":
      return "Recovered Amount"
    case "interactionsLogged":
      return "Interactions"
    case "clientsResolved":
      return "Clients Resolved"
    default:
      return "Recovery Rate"
  }
}

function trendSeries(summary: any) {
  const grouped = new Map<string, Record<string, string | number>>()

  for (const point of summary?.charts?.focusAgentTrends ?? []) {
    const current = (grouped.get(point.metricDate) ?? {
      metricDate: point.metricDate,
    }) as Record<string, string | number>
    current[String(point.agentName)] = point.recoveryRate
    grouped.set(point.metricDate, current)
  }

  for (const point of summary?.charts?.teamTrend ?? []) {
    const current = (grouped.get(point.metricDate) ?? {
      metricDate: point.metricDate,
    }) as Record<string, string | number>
    current.team = point.recoveryRate
    grouped.set(point.metricDate, current)
  }

  return [...grouped.values()]
}

const CHART_STROKES = ["#2563eb", "#0f766e", "#d97706", "#dc2626", "#0891b2", "#7c3aed"]

export function AdminAgentPerformancePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [distributionMetric, setDistributionMetric] = useState("recoveryRate")
  const [leaderboardSearch, setLeaderboardSearch] = useState(searchParams.get("search") ?? "")

  const canManage = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN"
  const selectedAgentId = searchParams.get("agent_id")
  const team = searchParams.get("team") ?? ""
  const band = searchParams.get("band") ?? ""
  const status = searchParams.get("status") ?? "ACTIVE"
  const overdueCategory = searchParams.get("overdue_category") ?? ""
  const period = searchParams.get("period") ?? "CURRENT"
  const sortBy = searchParams.get("sort_by") ?? "recovery_rate"
  const sortOrder = searchParams.get("sort_order") ?? "desc"

  const summaryParams = useMemo(
    () =>
      ({
        ...(team ? { team } : {}),
        ...(overdueCategory ? { overdue_category: overdueCategory } : {}),
        ...(period ? { period } : {}),
      }) as Record<string, string>,
    [overdueCategory, period, team],
  )

  const leaderboardParams = useMemo(
    () =>
      ({
        ...(team ? { team } : {}),
        ...(band ? { band } : {}),
        ...(status ? { status } : {}),
        ...(overdueCategory ? { overdue_category: overdueCategory } : {}),
        ...(period ? { period } : {}),
        ...(leaderboardSearch.trim() ? { search: leaderboardSearch.trim() } : {}),
        sort_by: sortBy,
        sort_order: sortOrder,
        per_page: "50",
      }) as Record<string, string>,
    [band, leaderboardSearch, overdueCategory, period, sortBy, sortOrder, status, team],
  )

  const summaryQuery = useQuery({
    queryKey: ["performance-team-summary", summaryParams],
    queryFn: () => getPerformanceTeamSummary(summaryParams),
  })

  const leaderboardQuery = useQuery({
    queryKey: ["performance-leaderboard", leaderboardParams],
    queryFn: () => getPerformanceLeaderboard(leaderboardParams),
  })

  const summary = summaryQuery.data?.data
  const leaderboard = leaderboardQuery.data?.data ?? []
  const distributionRows = summary?.charts?.distribution ?? []
  const chartRows = trendSeries(summary)

  function updateParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    setSearchParams(next)
  }

  function handleSort(nextSortBy: string) {
    if (sortBy === nextSortBy) {
      updateParam("sort_order", sortOrder === "asc" ? "desc" : "asc")
      return
    }
    updateParam("sort_by", nextSortBy)
    updateParam("sort_order", nextSortBy === "agent_name" ? "asc" : "desc")
  }

  return (
    <div className="space-y-6">
      <PageSection
        title="Agent Performance"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/targets")}>
              Set / Edit Targets
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-500">
          Performance analytics, target tracking, and corrective action across the collections team.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-5">
          <select
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            value={period}
            onChange={(event) => updateParam("period", event.target.value)}
          >
            <option value="CURRENT">Current period</option>
            <option value="PREVIOUS">Previous period</option>
          </select>
          <select
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            value={team}
            onChange={(event) => updateParam("team", event.target.value || null)}
          >
            <option value="">All teams</option>
            {(summary?.availableTeams ?? []).map((entry: string) => (
              <option key={entry} value={entry}>
                {entry}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            value={band}
            onChange={(event) => updateParam("band", event.target.value || null)}
          >
            <option value="">All bands</option>
            <option value="GREEN">Green</option>
            <option value="AMBER">Amber</option>
            <option value="RED">Red</option>
          </select>
          <select
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            value={status}
            onChange={(event) => updateParam("status", event.target.value)}
          >
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ALL">All</option>
          </select>
          <select
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            value={overdueCategory}
            onChange={(event) => updateParam("overdue_category", event.target.value || null)}
          >
            <option value="">All categories</option>
            <option value="S1">S-1</option>
            <option value="CATEGORY_A">Category A</option>
            <option value="CATEGORY_B">Category B</option>
            <option value="CATEGORY_C">Category C</option>
          </select>
        </div>
      </PageSection>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label="Total Agents"
          value={`${summary?.kpis?.activeAgents ?? 0} / ${summary?.kpis?.totalAgents ?? 0}`}
          helper={`${summary?.kpis?.inactiveAgents ?? 0} inactive`}
        />
        <StatCard
          label="Aggregate Recovery Rate"
          value={`${(summary?.kpis?.aggregateRecoveryRate ?? 0).toFixed(1)}%`}
        />
        <StatCard
          label="Total Recovered"
          value={formatCurrency(summary?.kpis?.totalRecovered ?? 0)}
        />
        <StatCard
          label="Avg Interactions / Agent"
          value={(summary?.kpis?.avgInteractionsPerAgent ?? 0).toFixed(1)}
        />
        <button
          type="button"
          className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
          onClick={() => updateParam("band", "GREEN")}
        >
          <p className="text-xs text-slate-500">Agents On Target</p>
          <p className="mt-2 text-2xl font-bold text-emerald-700">
            {summary?.kpis?.agentsOnTarget ?? 0}
          </p>
        </button>
        <button
          type="button"
          className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
          onClick={() => updateParam("band", "AMBER")}
        >
          <p className="text-xs text-slate-500">Agents At Risk</p>
          <p className="mt-2 text-2xl font-bold text-amber-700">
            {summary?.kpis?.agentsAtRisk ?? 0}
          </p>
        </button>
        <button
          type="button"
          className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
          onClick={() => updateParam("band", "RED")}
        >
          <p className="text-xs text-slate-500">Agents Behind</p>
          <p className="mt-2 text-2xl font-bold text-red-700">
            {summary?.kpis?.agentsBehind ?? 0}
          </p>
        </button>
        <button
          type="button"
          className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
          onClick={() => handleSort("unprocessed_count")}
        >
          <p className="text-xs text-slate-500">Total Unprocessed Clients</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">
            {summary?.kpis?.totalUnprocessedClients ?? 0}
          </p>
        </button>
      </div>

      {(summary?.alerts ?? []).length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {summary.alerts.map((alert: any) => (
            <button
              key={alert.key}
              type="button"
              className={`rounded-full border px-3 py-1.5 text-sm ${
                alert.severity === "danger"
                  ? "border-red-200 bg-red-50 text-red-700"
                  : alert.severity === "warning"
                    ? "border-amber-200 bg-amber-50 text-amber-700"
                    : "border-blue-200 bg-blue-50 text-blue-700"
              }`}
              onClick={() => {
                if (alert.actionFilter) {
                  updateParam("band", alert.actionFilter)
                  return
                }
                if (alert.agentId) {
                  updateParam("agent_id", alert.agentId)
                }
              }}
            >
              {alert.title}
            </button>
          ))}
        </div>
      ) : null}

      <PageSection
        title="Performance Leaderboard"
        actions={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search agent"
              value={leaderboardSearch}
              onChange={(event) => setLeaderboardSearch(event.target.value)}
            />
            <Button variant="outline" onClick={() => updateParam("search", leaderboardSearch || null)}>
              Apply
            </Button>
          </div>
        }
      >
        {leaderboardQuery.isLoading ? (
          <div className="h-80 animate-pulse rounded-xl bg-slate-100" />
        ) : leaderboard.length === 0 ? (
          <EmptyState title="No agents match filters" description="Adjust team, band, or status filters." />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <TableHead>
                <Th>Rank</Th>
                <Th>
                  <button type="button" onClick={() => handleSort("performance_band")}>
                    Band
                  </button>
                </Th>
                <Th>
                  <button type="button" onClick={() => handleSort("agent_name")}>
                    Agent
                  </button>
                </Th>
                <Th>Team</Th>
                <Th>
                  <button type="button" onClick={() => handleSort("assigned_clients")}>
                    Assigned
                  </button>
                </Th>
                <Th>
                  <button type="button" onClick={() => handleSort("outstanding_amount")}>
                    Outstanding
                  </button>
                </Th>
                <Th>
                  <button type="button" onClick={() => handleSort("recovered_amount")}>
                    Recovered
                  </button>
                </Th>
                <Th>
                  <button type="button" onClick={() => handleSort("recovery_rate")}>
                    Recovery Rate
                  </button>
                </Th>
                <Th>Target</Th>
                <Th>Gap</Th>
                <Th>Interactions</Th>
                <Th>Resolved</Th>
                <Th>Unprocessed</Th>
                <Th>Longest Gap</Th>
                <Th>Last Active</Th>
                <Th>Pipeline</Th>
                <Th>Trend</Th>
              </TableHead>
              <TableBody>
                {leaderboard.map((row: any) => (
                  <TableRow
                    key={row.agentId}
                    onClick={() => updateParam("agent_id", row.agentId)}
                  >
                    <Td>#{row.rank}</Td>
                    <Td>
                      <PerformanceBandBadge band={row.performanceBand} />
                    </Td>
                    <Td>
                      <button
                        type="button"
                        className="text-left"
                        onClick={(event) => {
                          event.stopPropagation()
                          updateParam("agent_id", row.agentId)
                        }}
                      >
                        <span className="font-semibold text-slate-900">{row.agentName}</span>
                        <span className="block text-xs text-slate-500">{row.agentId}</span>
                      </button>
                    </Td>
                    <Td>{row.teamName ?? "-"}</Td>
                    <Td>{row.assignedClients ?? 0}</Td>
                    <Td>{formatCurrency(row.outstandingAmount ?? 0)}</Td>
                    <Td>{formatCurrency(row.recoveredAmount ?? 0)}</Td>
                    <Td>
                      <div className="min-w-[140px]">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-semibold text-slate-900">
                            {(row.recoveryRate ?? 0).toFixed(1)}%
                          </span>
                          <span className="text-slate-500">
                            target {(row.targetPercentage ?? 0).toFixed(1)}%
                          </span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-slate-100">
                          <div
                            className={`h-full rounded-full ${
                              row.performanceBand === "GREEN"
                                ? "bg-emerald-500"
                                : row.performanceBand === "AMBER"
                                  ? "bg-amber-500"
                                  : "bg-red-500"
                            }`}
                            style={{
                              width: `${Math.min(
                                100,
                                row.targetPercentage > 0
                                  ? (row.recoveryRate / row.targetPercentage) * 100
                                  : 0,
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    </Td>
                    <Td>{row.targetPercentage?.toFixed(1) ?? "-"}%</Td>
                    <Td className={(row.gapToTarget ?? 0) >= 0 ? "text-emerald-700" : "text-red-700"}>
                      {(row.gapToTarget ?? 0) >= 0 ? "+" : ""}
                      {(row.gapToTarget ?? 0).toFixed(1)}%
                    </Td>
                    <Td>{row.interactionsLogged ?? 0}</Td>
                    <Td>{row.clientsResolved ?? 0}</Td>
                    <Td>{row.unprocessedCount ?? 0}</Td>
                    <Td>{row.longestUnprocessedSeconds ?? 0}s</Td>
                    <Td>{formatDateTime(row.lastSeenAt)}</Td>
                    <Td>
                      <div className="min-w-[140px]">
                        <PipelineMiniBar values={row} />
                      </div>
                    </Td>
                    <Td>
                      <TrendSparkline points={row.trendPoints ?? []} />
                    </Td>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </div>
        )}
      </PageSection>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <PageSection
          title="Recovery Distribution"
          actions={
            <select
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              value={distributionMetric}
              onChange={(event) => setDistributionMetric(event.target.value)}
            >
              <option value="recoveryRate">Recovery Rate</option>
              <option value="recoveredAmount">Recovered Amount</option>
              <option value="interactionsLogged">Interactions</option>
              <option value="clientsResolved">Clients Resolved</option>
            </select>
          }
        >
          {summaryQuery.isLoading ? (
            <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
          ) : distributionRows.length === 0 ? (
            <EmptyState title="No chart data" description="No performance distribution was returned." />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={distributionRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="agentName" hide />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey={distributionMetric} name={metricLabel(distributionMetric)}>
                    {distributionRows.map((row: any) => (
                      <Cell
                        key={row.agentId}
                        fill={
                          row.performanceBand === "GREEN"
                            ? "#059669"
                            : row.performanceBand === "AMBER"
                              ? "#d97706"
                              : "#dc2626"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </PageSection>

        <PageSection title="Recovery Trend">
          {summaryQuery.isLoading ? (
            <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
          ) : chartRows.length === 0 ? (
            <EmptyState title="No trend data" description="Trend lines will appear once daily metrics exist." />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartRows}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="metricDate" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="team"
                    stroke="#0f172a"
                    strokeDasharray="6 4"
                    strokeWidth={2.5}
                    name="Team"
                  />
                  {([
                    ...new Set((summary?.charts?.focusAgentTrends ?? []).map((row: any) => row.agentName)),
                  ] as string[])
                    .slice(0, 10)
                    .map((agentName: string, index: number) => (
                      <Line
                        key={agentName}
                        type="monotone"
                        dataKey={agentName}
                        dot={false}
                        stroke={CHART_STROKES[index % CHART_STROKES.length]}
                        strokeWidth={2}
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </PageSection>
      </div>

      <AgentPerformanceDrawer
        agentId={selectedAgentId}
        open={Boolean(selectedAgentId)}
        canManage={canManage}
        onClose={() => updateParam("agent_id", null)}
      />
    </div>
  )
}
